import { useImperativeHandle } from 'react'
import type { Swiper as SwiperType } from 'swiper'

import type { MonthData } from '~entities/index'
import { getLeftEdgeSlideIndex } from '~sharedLib'
import type { MonthNavigationRef } from '../../types'

interface UseNavigationHandlersProps {
	ref: React.ForwardedRef<MonthNavigationRef>
	swiperRef: React.RefObject<SwiperType | null>
	months: MonthData[]
	emitCurrentMonth: () => void
	tryAddPrevMonth: () => void
	isProgrammaticScrollRef: React.RefObject<boolean>
}

export const useNavigationHandlers = ({
	ref,
	swiperRef,
	months,
	emitCurrentMonth,
	tryAddPrevMonth,
	isProgrammaticScrollRef
}: UseNavigationHandlersProps) => {
	useImperativeHandle(ref, () => ({
		get swiper() {
			return swiperRef.current
		},
		slidePrev: () => {
			const swiper = swiperRef.current
			if (!swiper) return
			isProgrammaticScrollRef.current = true

			const currentIndex = getLeftEdgeSlideIndex(swiper)
			if (currentIndex > 0)
				swiper.slideTo(currentIndex - 1, 500)
			else
				tryAddPrevMonth()
			requestAnimationFrame(() => {
				isProgrammaticScrollRef.current = false
				emitCurrentMonth()
			})
		},
		slideNext: async () => {
			const swiper = swiperRef.current
			if (!swiper) return
			isProgrammaticScrollRef.current = true

			const currentIndex = getLeftEdgeSlideIndex(swiper)
			const targetIndex = currentIndex + 1

			while (getLeftEdgeSlideIndex(swiper) < targetIndex) {
				if (!months[targetIndex]) {
					await new Promise((res) => requestAnimationFrame(res))
					swiper.update()
				}

				const newIndex = getLeftEdgeSlideIndex(swiper)
				if (newIndex >= targetIndex) break

				const nextSlide = swiper.slides[newIndex + 1] as HTMLElement
				if (!nextSlide) break

				swiper.slideTo(newIndex + 1, 500)
				await new Promise((res) => setTimeout(res, 500))
			}

			requestAnimationFrame(() => {
				isProgrammaticScrollRef.current = false
				emitCurrentMonth()
			})
		}
	}), [emitCurrentMonth, tryAddPrevMonth, months, swiperRef])
}
